import React, { useState } from 'react';
import { api } from '../api.js';
import { Card, ErrorBanner, Loading, Modal, Money, ProofReference, StateChip, SummaryList, useAsync, useSession, useToast } from '../ui.jsx';
import { auditLabel, centsToInput, dateLabel, monthLabel, money, nameOf, parseAmountToCents, recipientLabel, timestampLabel, unitName } from '../lib/format.js';
import { SubmitProofModal } from './member-home.jsx';

export default function ItemDetail({ params }) {
  const session = useSession();
  const notify = useToast();
  const item = useAsync(() => api.item(params.id), [params.id]);
  const [dialog, setDialog] = useState(null);
  const [error, setError] = useState(null);

  if (item.loading) return <Loading />;
  if (item.error) return <ErrorBanner error={item.error} />;

  const view = item.data.item;
  const isAdmin = session.member.role === 'admin';
  const isConfirmer = view.confirmer_member_id === session.member.id;
  const myDue = view.dues.find((due) => due.member_id === session.member.id);
  const reload = () => { setDialog(null); item.reload(); };

  const act = async (action, successMessage) => {
    setError(null);
    try {
      await action();
      notify(successMessage);
      reload();
    } catch (failure) {
      setError(failure);
    }
  };

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <div className="eyebrow">{unitName(session.all_units, view.unit_id)} · {view.category}</div>
          <h1 data-testid="item-title">{view.title}</h1>
          <p className="small muted">
            帳務月份 {monthLabel(view.accounting_month)} · 到期 {dateLabel(view.due_date)} · 建立者 {nameOf(session.members, view.creator_member_id)}
            {view.origin === 'recurrence' ? ' · 由循環設定產生' : ''}
            {view.historical ? ' · 歷史補錄' : ''}
          </p>
        </div>
        <div className="actions">
          {isAdmin && view.status === 'issued' && !view.historical ? (
            <>
              <button type="button" onClick={() => setDialog({ kind: 'correct' })} data-testid="item-correct">修改標題／到期日</button>
              <button type="button" onClick={() => setDialog({ kind: 'revise' })} data-testid="item-revise">修改總額／分攤</button>
              <button type="button" className="danger" onClick={() => setDialog({ kind: 'cancel' })} data-testid="item-cancel">取消項目</button>
            </>
          ) : null}
        </div>
      </div>

      <ErrorBanner error={error} />

      {view.status === 'cancelled' ? (
        <div className="banner warn" data-testid="item-cancelled-banner">
          已取消 · 原因：{view.cancel_reason} · 取消日期 {dateLabel(view.cancelled_on)} · 操作者 {nameOf(session.members, view.cancelled_by)}
          <div className="small">已取消的項目不再追收，但金額、付款證明、操作者及修改紀錄完整保留。</div>
        </div>
      ) : null}

      <Card title="項目資料" testId="item-summary">
        <SummaryList rows={[
          ['單位', unitName(session.all_units, view.unit_id)],
          ['分類', view.category],
          ['總額', money(view.total_cents)],
          ['金額類型', view.amount_kind === 'floating' ? '浮動金額' : '固定金額'],
          ['收款人', recipientLabel(session.members, view)],
          ['確認人', view.historical ? '—（歷史補錄不設 App 確認人）' : nameOf(session.members, view.confirmer_member_id)],
          ['發出日期', dateLabel(view.issue_date)],
          ['到期日', dateLabel(view.due_date)],
          view.historical ? ['歷史結果', view.historical_outcome === 'settled' ? '已結清' : '已取消'] : null,
          view.historical ? ['補錄者', `${nameOf(session.members, view.recorded_by)} · ${timestampLabel(view.recorded_at)}`] : null,
        ]} />
        {view.bill_proof_id ? (
          <p className="small" style={{ marginTop: 10 }}>
            帳單證明（說明項目總額，不代表任何成員已付款）：<ProofReference reference={view.bill_proof_id} />
          </p>
        ) : null}
      </Card>

      {myDue ? (
        <Card
          title="我的應付款"
          testId="my-due"
          actions={view.status === 'issued' && !view.historical && myDue.remaining_cents > 0 ? (
            <button type="button" className="primary" onClick={() => setDialog({ kind: 'submit' })} data-testid="my-due-submit">提交付款證明</button>
          ) : null}
        >
          <div className="grid three">
            <div className="metric"><span>應付款</span><strong>{money(myDue.amount_cents)}</strong></div>
            <div className="metric"><span>已確認</span><strong data-testid="my-confirmed">{money(myDue.confirmed_cents)}</strong></div>
            <div className="metric"><span>待確認</span><strong>{money(myDue.pending_cents)}</strong></div>
            <div className="metric"><span>仍待付款</span><strong data-testid="my-remaining">{money(myDue.remaining_cents)}</strong></div>
            <div className="metric"><span>待處理結餘</span><strong data-testid="my-balance">{money(myDue.pending_balance_cents)}</strong></div>
            <div className="metric"><span>狀態</span><StateChip state={myDue.state} label={myDue.state_label} overdue={myDue.overdue} /></div>
          </div>
        </Card>
      ) : null}

      {view.dues.map((due) => (
        <Card
          key={due.due_id}
          title={`${nameOf(session.members, due.member_id)} 的付款紀錄`}
          subtitle={`應付款 ${money(due.amount_cents)} · 已確認 ${money(due.confirmed_cents)} · 仍待付款 ${money(due.remaining_cents)}`}
          testId={`due-${nameOf(session.members, due.member_id)}`}
          actions={isAdmin && view.status === 'issued' && !view.historical ? (
            <button type="button" className="small" onClick={() => setDialog({ kind: 'adjust', due })} data-testid={`adjust-${nameOf(session.members, due.member_id)}`}>調整應付款</button>
          ) : null}
        >
          <div className="row" style={{ marginBottom: 10 }}>
            <StateChip state={due.state} label={due.state_label} overdue={due.overdue} />
          </div>

          {due.submissions.length === 0 && due.historical_payments.length === 0 ? (
            <p className="small muted">尚未有付款紀錄。</p>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>證明參考</th>
                    <th className="num">金額</th>
                    <th>狀態</th>
                    <th>日期</th>
                    <th>備註</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {due.submissions.map((submission) => (
                    <tr key={submission.id} data-testid={`submission-${submission.reference}`}>
                      <td><ProofReference reference={submission.reference} /></td>
                      <td className="num">{money(submission.amount_cents)}</td>
                      <td><StateChip state={submission.state} label={submission.state_label} /></td>
                      <td className="small">
                        提交 {dateLabel(submission.submitted_on)}
                        {submission.decided_on ? <div>處理 {dateLabel(submission.decided_on)}</div> : null}
                      </td>
                      <td className="small">{submission.return_reason || '—'}</td>
                      <td>
                        {isConfirmer && submission.state === 'pending' ? (
                          <div className="row">
                            <button
                              type="button"
                              className="primary small"
                              data-testid={`confirm-${submission.reference}`}
                              onClick={() => act(() => api.confirmSubmission(submission.id), '付款提交已確認')}
                            >
                              確認
                            </button>
                            <button
                              type="button"
                              className="small"
                              data-testid={`return-${submission.reference}`}
                              onClick={() => setDialog({ kind: 'return', submission })}
                            >
                              退回
                            </button>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                  {due.historical_payments.map((payment) => (
                    <tr key={payment.id} data-testid={`historical-payment-${payment.reference}`}>
                      <td>{payment.reference}</td>
                      <td className="num">{money(payment.amount_cents)}</td>
                      <td><span className="chip settled">已確認（歷史補錄）</span></td>
                      <td className="small">付款 {dateLabel(payment.paid_on)}</td>
                      <td className="small">
                        {payment.note || '—'}
                        <div className="muted">補錄者 {nameOf(session.members, payment.recorded_by)} · {timestampLabel(payment.recorded_at)}</div>
                      </td>
                      <td><ProofReference reference={payment.reference} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {due.adjustments.length ? (
            <div className="small" style={{ marginTop: 10 }} data-testid={`adjustments-${nameOf(session.members, due.member_id)}`}>
              <strong>調整應付款紀錄</strong>
              <ul className="audit-list">
                {due.adjustments.map((adjustment) => (
                  <li key={adjustment.id}>
                    {money(adjustment.old_amount_cents)} → {money(adjustment.new_amount_cents)} ·
                    原因：{adjustment.reason} · 操作者 {nameOf(session.members, adjustment.actor_member_id)} · {timestampLabel(adjustment.created_at)}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {due.balances.length ? (
            <div className="small" style={{ marginTop: 10 }} data-testid={`balances-${nameOf(session.members, due.member_id)}`}>
              <strong>結餘</strong>
              <ul className="audit-list">
                {due.balances.map((balance) => (
                  <li key={balance.id}>
                    {money(balance.amount_cents)} · {balance.pending_cents > 0 ? `待處理 ${money(balance.pending_cents)}` : '已完成處理'}
                    {balance.dispositions.map((disposition) => (
                      <div key={disposition.id}>
                        {disposition.kind === 'returned' ? '已退回' : '已轉用'} {money(disposition.amount_cents)} · 處理日期 {dateLabel(disposition.handled_on)}
                        {disposition.reason ? ` · ${disposition.reason}` : ''}
                      </div>
                    ))}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </Card>
      ))}

      {isAdmin ? <AuditTrail itemId={view.id} /> : null}

      {dialog?.kind === 'submit' ? (
        <SubmitProofModal
          entry={{ ...view, item_id: view.id, due: myDue }}
          onClose={() => setDialog(null)}
          onDone={reload}
        />
      ) : null}
      {dialog?.kind === 'return' ? (
        <ReasonModal
          title="退回付款提交"
          subtitle={`${dialog.submission.reference} · ${money(dialog.submission.amount_cents)}`}
          note="退回會保留原提交的金額、圖片及原因；成員可另交新提交。"
          onClose={() => setDialog(null)}
          onSubmit={(reason) => act(() => api.returnSubmission(dialog.submission.id, { reason }), '付款提交已退回')}
        />
      ) : null}
      {dialog?.kind === 'cancel' ? (
        <ReasonModal
          title="取消付款項目"
          subtitle={view.title}
          note="取消會停止追收，但保留所有金額與付款證明。待確認提交會標記為因取消終止，已確認付款會成為待處理結餘。"
          confirmLabel="再次確認並取消項目"
          requireDoubleConfirm
          onClose={() => setDialog(null)}
          onSubmit={(reason) => act(() => api.cancelItem(view.id, { reason, confirm: true }), '付款項目已取消')}
        />
      ) : null}
      {dialog?.kind === 'adjust' ? (
        <AdjustModal
          due={dialog.due}
          memberName={nameOf(session.members, dialog.due.member_id)}
          onClose={() => setDialog(null)}
          onSubmit={(payload) => act(() => api.adjustDue(dialog.due.due_id, payload), '應付款已調整')}
        />
      ) : null}
      {dialog?.kind === 'correct' ? (
        <CorrectModal view={view} onClose={() => setDialog(null)} onSubmit={(payload) => act(() => api.correctItem(view.id, payload), '已修改並保留留痕')} />
      ) : null}
      {dialog?.kind === 'revise' ? (
        <ReviseModal view={view} onClose={() => setDialog(null)} onSubmit={(payload) => act(() => api.reviseItem(view.id, payload), '已修改總額及分攤')} />
      ) : null}
    </div>
  );
}

function AuditTrail({ itemId }) {
  const session = useSession();
  const audit = useAsync(() => api.itemAudit(itemId), [itemId]);
  return (
    <Card title="留痕紀錄" subtitle="每項受權限保護的改動都保留操作者、UTC 時間、原因及前後內容" testId="audit-trail">
      {audit.loading ? <Loading /> : null}
      {audit.error ? <ErrorBanner error={audit.error} /> : null}
      <ul className="audit-list">
        {(audit.data ? audit.data.events : []).map((event) => (
          <li key={event.id} data-testid={`audit-${event.action}`}>
            <strong>{auditLabel(event.action)}</strong> · {nameOf(session.members, event.actor_member_id) } · {timestampLabel(event.created_at)}
            {event.reason ? <div>原因：{event.reason}</div> : null}
            {event.before_json ? <pre className="json">前：{event.before_json}</pre> : null}
            {event.after_json ? <pre className="json">後：{event.after_json}</pre> : null}
          </li>
        ))}
      </ul>
    </Card>
  );
}

export function ReasonModal({ title, subtitle, note, confirmLabel = '確認', requireDoubleConfirm, onClose, onSubmit }) {
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(!requireDoubleConfirm);
  return (
    <Modal title={title} subtitle={subtitle} onClose={onClose}>
      {note ? <div className="banner warn small" style={{ marginBottom: 12 }}>{note}</div> : null}
      <label className="field">
        <span>原因（必填）</span>
        <textarea value={reason} onChange={(event) => setReason(event.target.value)} data-testid="reason-input" />
      </label>
      {requireDoubleConfirm ? (
        <label className="row small" style={{ marginTop: 10 }}>
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={confirmed}
            onChange={(event) => setConfirmed(event.target.checked)}
            data-testid="double-confirm"
          />
          我已檢查並確認要執行此操作
        </label>
      ) : null}
      <div className="modal-actions">
        <button type="button" onClick={onClose}>返回</button>
        <button
          type="button"
          className="danger"
          disabled={reason.trim().length < 2 || !confirmed}
          onClick={() => onSubmit(reason.trim())}
          data-testid="reason-submit"
        >
          {confirmLabel}
        </button>
      </div>
    </Modal>
  );
}

function AdjustModal({ due, memberName, onClose, onSubmit }) {
  const [amount, setAmount] = useState(centsToInput(due.amount_cents));
  const [reason, setReason] = useState('');
  const cents = parseAmountToCents(amount);
  const projectedBalance = cents === null ? 0 : Math.max(0, due.confirmed_cents + due.credited_cents - cents);
  return (
    <Modal title="調整應付款" subtitle={`${memberName} · 現時應付款 ${money(due.amount_cents)}`} onClose={onClose}>
      <div className="banner info small" style={{ marginBottom: 12 }}>
        調整會保留舊值、新值、原因、操作者及時間，並不會改寫或失效既有已確認付款。
      </div>
      <div className="stack">
        <label className="field">
          <span>調整後應付款（HKD）</span>
          <input value={amount} onChange={(event) => setAmount(event.target.value)} inputMode="decimal" data-testid="adjust-amount" />
        </label>
        <label className="field">
          <span>原因（必填）</span>
          <textarea value={reason} onChange={(event) => setReason(event.target.value)} data-testid="adjust-reason" />
        </label>
        <div className="small muted">
          已確認付款 {money(due.confirmed_cents)}；
          {projectedBalance > 0
            ? `調整後會產生待處理結餘 ${money(projectedBalance)}。`
            : cents !== null && cents > due.amount_cents ? '調整後仍欠金額會增加。' : '調整後不會產生結餘。'}
        </div>
      </div>
      <div className="modal-actions">
        <button type="button" onClick={onClose}>取消</button>
        <button
          type="button"
          className="primary"
          disabled={cents === null || reason.trim().length < 2}
          onClick={() => onSubmit({ new_amount_cents: cents, reason: reason.trim() })}
          data-testid="adjust-submit"
        >
          儲存調整
        </button>
      </div>
    </Modal>
  );
}

function CorrectModal({ view, onClose, onSubmit }) {
  const [title, setTitle] = useState(view.title);
  const [dueDate, setDueDate] = useState(view.due_date);
  const [reason, setReason] = useState('');
  return (
    <Modal title="修改標題及到期日" subtitle="文書修正會保留前後值、操作者及 UTC 時間" onClose={onClose}>
      <div className="stack">
        <label className="field"><span>項目名稱</span><input value={title} onChange={(event) => setTitle(event.target.value)} data-testid="correct-title" /></label>
        <label className="field"><span>到期日</span><input type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} data-testid="correct-due-date" /></label>
        <label className="field"><span>原因（選填）</span><input value={reason} onChange={(event) => setReason(event.target.value)} /></label>
      </div>
      <div className="modal-actions">
        <button type="button" onClick={onClose}>取消</button>
        <button type="button" className="primary" onClick={() => onSubmit({ title, due_date: dueDate, reason })} data-testid="correct-submit">儲存</button>
      </div>
    </Modal>
  );
}

function ReviseModal({ view, onClose, onSubmit }) {
  const session = useSession();
  const [total, setTotal] = useState(centsToInput(view.total_cents));
  const [allocations, setAllocations] = useState(
    view.dues.filter((due) => due.amount_cents > 0).map((due) => ({ member_id: due.member_id, amount: centsToInput(due.amount_cents) })),
  );
  const [reason, setReason] = useState('');
  const totalCents = parseAmountToCents(total);
  const allocationCents = allocations.map((allocation) => parseAmountToCents(allocation.amount));
  const sum = allocationCents.reduce((accumulated, value) => accumulated + (value || 0), 0);
  const balanced = totalCents !== null && !allocationCents.includes(null) && sum === totalCents;

  return (
    <Modal title="修改總額及分攤" subtitle="只在未有任何已確認付款時可用" onClose={onClose}>
      <div className="stack">
        <label className="field"><span>總額（HKD）</span><input value={total} onChange={(event) => setTotal(event.target.value)} data-testid="revise-total" /></label>
        {allocations.map((allocation, index) => (
          <label className="field" key={allocation.member_id}>
            <span>{nameOf(session.members, allocation.member_id)} 分攤</span>
            <input
              value={allocation.amount}
              data-testid={`revise-allocation-${allocation.member_id}`}
              onChange={(event) => setAllocations(allocations.map((entry, position) => (
                position === index ? { ...entry, amount: event.target.value } : entry
              )))}
            />
          </label>
        ))}
        <label className="field"><span>原因（選填）</span><input value={reason} onChange={(event) => setReason(event.target.value)} /></label>
        {!balanced ? <div className="banner warn small">各成員分攤合計必須等於總額。</div> : null}
      </div>
      <div className="modal-actions">
        <button type="button" onClick={onClose}>取消</button>
        <button
          type="button"
          className="primary"
          disabled={!balanced}
          data-testid="revise-submit"
          onClick={() => onSubmit({
            total_cents: totalCents,
            allocations: allocations.map((allocation) => ({
              member_id: allocation.member_id,
              amount_cents: parseAmountToCents(allocation.amount),
            })),
            reason,
          })}
        >
          儲存
        </button>
      </div>
    </Modal>
  );
}
